import { Injectable } from "@angular/core";

import { openDB, DBSchema, IDBPDatabase } from "idb";
import { Device } from "./device";

interface CryptoDB extends DBSchema {
  ckeys: {
    value: Device;
    key: string;
  };
}

const HASH = "SHA-256";
const ALGORITHH = "ECDSA";
const CURVE = "P-256";

@Injectable({
  providedIn: "root",
})
export class CryptoService {
  ckp: CryptoKeyPair | undefined;
  db!: IDBPDatabase<CryptoDB>;

  constructor() {
    this.LoadDB();
  }

  async LoadDB() {
    this.db = await openDB<CryptoDB>("cryptodb", 1, {
      upgrade(db: any) {
        db.createObjectStore("ckeys");
      },
    });
  }

  async GetDevice(username: string) {
    if (!this.db) {
      await this.LoadDB();
    }
    var kp: any = this.db.get("ckeys", username);
    return kp;
  }

  async RemoveDevice(username: string) {
    this.db.delete("ckeys", username);
  }

  async PersistDevice(device: Device) {
    this.db.put("ckeys", device, device.UserName);
  }

  async Sign(device: Device, data: any) {
    if (typeof data !== "string") {
      data = JSON.stringify(data);
    }
    var enc = new TextEncoder();
    var sbytes = await window.crypto.subtle.sign(
      {
        name: ALGORITHH,
        hash: { name: HASH },
      },
      device.KeyPair.privateKey,
      enc.encode(data)
    );
    var signature = new Uint8Array(sbytes);
    var desSign = this.toDER(signature);
    return desSign;
  }

  async GetWorkspace(workspace: string) {
    if (!this.db) {
      await this.LoadDB();
    }
    var kp: any = this.db.get("ckeys", workspace);
    return kp;
  }

  async RemoveWorkspace(workspace: string) {
    this.db.delete("ckeys", workspace);
  }

  async PersistWorkspace(workspace: Device) {
    this.db.put("ckeys", workspace, workspace.WorkSpace);
  }

  async WSign(pk: any, data: any) {
    if (typeof data !== "string") {
      data = JSON.stringify(data);
    }
    var enc = new TextEncoder();
    var sbytes = await window.crypto.subtle.sign(
      {
        name: ALGORITHH,
        hash: { name: HASH },
      },
      pk,
      enc.encode(data)
    );
    var signature = new Uint8Array(sbytes);
    var desSign = this.toDER(signature);
    return desSign;
  }

  pemToArrayBuffer(pem: string): ArrayBuffer {
    const b64 = pem
      .replace(/-----BEGIN PRIVATE KEY-----/, "")
      .replace(/-----END PRIVATE KEY-----/, "")
      .replace(/\s+/g, "");
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async buildKeyPairFromPEMAndBase64(
    privateKeyPEM: string
  ): Promise<{ privateKey: CryptoKey; publicKey: CryptoKey | null }> {
    const privateKey = await crypto.subtle.importKey(
      "pkcs8",
      this.pemToArrayBuffer(privateKeyPEM),
      {
        name: "ECDSA",
        namedCurve: "P-256",
      },
      false,
      ["sign"]
    );

    return { privateKey, publicKey: null };
  }

  rmPadding(buf: any) {
    var i = 0;
    var len = buf.length - 1;
    while (!buf[i] && !(buf[i + 1] & 0x80) && i < len) {
      i++;
    }
    if (i === 0) {
      return buf;
    }
    return buf.slice(i);
  }

  constructLength(arr: any, len: any) {
    if (len < 0x80) {
      arr.push(len);
      return;
    }
    var octets = 1 + ((Math.log(len) / Math.LN2) >>> 3);
    arr.push(octets | 0x80);
    while (--octets) {
      arr.push((len >>> (octets << 3)) & 0xff);
    }
    arr.push(len);
  }

  toDER(inp: Uint8Array) {
    var r = Array.from(inp.slice(0, 32));
    var s = Array.from(inp.slice(32));

    if (r[0] & 0x80) r = [0].concat(r);
    if (s[0] & 0x80) s = [0].concat(s);

    r = this.rmPadding(r);
    s = this.rmPadding(s);

    while (!s[0] && !(s[1] & 0x80)) {
      s = s.slice(1);
    }
    var arr = [0x02];
    this.constructLength(arr, r.length);
    arr = arr.concat(r);
    arr.push(0x02);
    this.constructLength(arr, s.length);
    var backHalf = arr.concat(s);
    var res = [0x30];
    this.constructLength(res, backHalf.length);
    res = res.concat(backHalf);
    let body = window.btoa(String.fromCharCode(...res));
    return body;
  }
}
